import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { GoogleLogo } from "@phosphor-icons/react";
import { useApp } from "@/context/AppContext";
import { Logo } from "@/components/Logo";
import { PasswordStrength } from "@/components/PasswordStrength";
import { Footer } from "@/components/Footer";
import api from "@/lib/api";

export default function Login() {
  const { t, loginWithToken, simpleMode, setSimpleMode } = useApp();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login"); // login | register | forgot
  const [form, setForm] = useState({ email: "", password: "", first_name: "", last_name: "" });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email: form.email });
        toast.success(t("resetSent"));
        setMode("login");
      } else if (mode === "register") {
        if (!acceptTerms) { toast.error(t("mustAcceptTerms")); setLoading(false); return; }
        const res = await api.post("/auth/register", form);
        loginWithToken(res.data.token, res.data.user);
        navigate("/dashboard");
      } else {
        const res = await api.post("/auth/login", { email: form.email, password: form.password });
        loginWithToken(res.data.token, res.data.user);
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setLoading(false);
    }
  };

  const startGuest = async () => {
    setLoading(true);
    try {
      const res = await api.post("/auth/guest");
      loginWithToken(res.data.token, res.data.user);
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fehler");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="mx-auto max-w-md min-h-[100dvh] flex flex-col justify-center px-6 py-10 sm:border-x-2 sm:border-gray-800" style={{ background: "var(--app-bg)" }}>
      <div className="sb-pop flex flex-col items-center mb-8">
        <Logo className="h-32 mb-4 drop-shadow-xl" />
        <p className="font-body text-gray-600 text-center">{t("tagline")}</p>
      </div>

      <div className="flex justify-center mb-6">
        <button data-testid="login-simple-toggle" onClick={() => setSimpleMode(!simpleMode)}
          className="px-4 py-2.5 rounded-full border-2 border-gray-800 bg-white font-body text-sm flex items-center gap-2 active:scale-95 transition-transform shadow-[2px_2px_0_0_rgba(31,41,55,1)]">
          <span className="font-heading font-medium text-gray-800">{t("simpleModeLabel")}</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-heading" style={simpleMode ? { background: "var(--app-primary)", color: "#fff" } : { background: "#F3F4F6", color: "#6B7280" }}>{simpleMode ? t("simpleOn") : t("simpleOff")}</span>
        </button>
      </div>

      <div className="sb-card p-6 sb-pop">
        {mode === "forgot" ? (
          <>
            <h2 className="text-xl font-heading font-semibold text-gray-900 mb-1">{t("forgotPassword")}</h2>
            <p className="font-body text-sm text-gray-500 mb-5">{t("resetEmailPrompt")}</p>
            <form onSubmit={submit} className="space-y-3">
              <input data-testid="forgot-email" type="email" className="sb-input" placeholder={t("email")} value={form.email} onChange={upd("email")} required />
              <button data-testid="forgot-submit" type="submit" disabled={loading}
                className="sb-btn w-full py-3.5 text-white font-medium text-lg disabled:opacity-60" style={{ background: "var(--app-primary)" }}>
                {loading ? "…" : t("sendResetLink")}
              </button>
            </form>
            <button data-testid="back-to-login" onClick={() => setMode("login")}
              className="mt-5 w-full text-center font-body text-gray-600 underline">{t("backToLogin")}</button>
          </>
        ) : (
          <>
            <div className="flex gap-2 mb-6">
              <button data-testid="tab-login" onClick={() => setMode("login")}
                className={`flex-1 py-2 rounded-xl font-heading font-medium border-2 border-gray-800 ${mode === "login" ? "text-white" : "bg-white text-gray-700"}`}
                style={mode === "login" ? { background: "var(--app-primary)" } : {}}>{t("login")}</button>
              <button data-testid="tab-register" onClick={() => setMode("register")}
                className={`flex-1 py-2 rounded-xl font-heading font-medium border-2 border-gray-800 ${mode === "register" ? "text-white" : "bg-white text-gray-700"}`}
                style={mode === "register" ? { background: "var(--app-primary)" } : {}}>{t("register")}</button>
            </div>

            <form onSubmit={submit} className="space-y-3">
              {mode === "register" && (
                <div className="flex gap-3">
                  <input data-testid="input-firstname" className="sb-input" placeholder={t("firstName")} value={form.first_name} onChange={upd("first_name")} required />
                  <input data-testid="input-lastname" className="sb-input" placeholder={t("lastName")} value={form.last_name} onChange={upd("last_name")} />
                </div>
              )}
              <input data-testid="input-email" type="email" className="sb-input" placeholder={t("email")} value={form.email} onChange={upd("email")} required />
              <input data-testid="input-password" type="password" className="sb-input" placeholder={t("password")} value={form.password} onChange={upd("password")} required />
              {mode === "register" && <PasswordStrength value={form.password} />}
              {mode === "register" && (
                <label data-testid="accept-terms-label" className="flex items-start gap-2 cursor-pointer">
                  <input data-testid="accept-terms" type="checkbox" className="mt-0.5 h-4 w-4 accent-[var(--app-primary)]" checked={acceptTerms} onChange={(e) => setAcceptTerms(e.target.checked)} />
                  <span className="font-body text-xs text-gray-600">
                    Ich akzeptiere die <a href="/terms" target="_blank" rel="noreferrer" className="underline">Nutzungsbedingungen</a> und die <a href="/privacy" target="_blank" rel="noreferrer" className="underline">Datenschutzerklärung</a>.
                  </span>
                </label>
              )}
              <button data-testid="submit-auth" type="submit" disabled={loading || (mode === "register" && !acceptTerms)}
                className="sb-btn w-full py-3.5 text-white font-medium text-lg disabled:opacity-60" style={{ background: "var(--app-primary)" }}>
                {loading ? "…" : mode === "login" ? t("login") : t("register")}
              </button>
            </form>

            <button data-testid="guest-login-btn" onClick={startGuest} disabled={loading}
              className="sb-btn w-full py-3.5 mt-3 font-medium text-lg disabled:opacity-60 flex items-center justify-center gap-2"
              style={{ background: "var(--app-strong)", color: "#fff" }}>
              {t("guestTryFree")}
            </button>

            {mode === "login" && (
              <button data-testid="forgot-link" onClick={() => setMode("forgot")}
                className="mt-3 w-full text-center font-body text-sm text-gray-500 underline">{t("forgotPassword")}</button>
            )}

            <div className="flex items-center gap-3 my-5">
              <div className="h-0.5 flex-1 bg-gray-200" />
              <span className="text-xs font-heading uppercase text-gray-400">{t("or")}</span>
              <div className="h-0.5 flex-1 bg-gray-200" />
            </div>

            <button data-testid="google-signin" onClick={google}
              className="sb-btn w-full py-3.5 bg-white font-medium flex items-center justify-center gap-2 text-gray-800">
              <GoogleLogo size={22} weight="bold" /> {t("googleSignIn")}
            </button>
          </>
        )}
      </div>

      {mode !== "forgot" && (
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="mt-6 text-center font-body text-gray-600 underline">
          {mode === "login" ? t("noAccount") : t("haveAccount")}
        </button>
      )}

      <Footer />
    </div>
  );
}
